/**
 * @fileoverview Slug utilities — shared across the ParcelPuffin ecosystem
 * @package @puffin-core/id-generator
 *
 * Canonical slug generation for entities that need human-friendly URLs
 * alongside their raw prefixed IDs (EPI-001). Pure functions — no I/O.
 *
 * Callers persist generated slugs on the entity row (e.g.
 * `planner_sales_plans.slug`) and resolve them on read via
 * `@puffin-core/database/resolveIdOrSlug`.
 *
 * Design:
 *   - lowercase ASCII + digits + hyphen only
 *   - max 80 chars total (matches VARCHAR(80) database column)
 *   - diacritic folding via `String.prototype.normalize('NFKD')`
 *   - collapsed hyphens, no leading/trailing hyphens
 *   - empty string input → empty slug (caller should supply a fallback part)
 */

'use strict';

const MAX_SLUG_LEN = 80;

/**
 * Convert an arbitrary string to a URL-safe slug segment.
 *
 *   slugify('November 2026 Plan — Downtown Retail')
 *     → 'november-2026-plan-downtown-retail'
 *   slugify('Café Français')
 *     → 'cafe-francais'
 *   slugify('')           → ''
 *   slugify(null)         → ''
 *
 * Preserves only [a-z0-9-]. Every run of disallowed characters collapses to
 * a single hyphen. Leading and trailing hyphens are stripped.
 *
 * @param {*} input - any value; coerced to string
 * @param {number} [maxLen=MAX_SLUG_LEN]
 * @returns {string}
 */
function slugify(input, maxLen = MAX_SLUG_LEN) {
  if (input === null || input === undefined) return '';
  const str = String(input);
  if (str.length === 0) return '';

  // 1. Normalise Unicode to NFKD and strip combining marks (diacritics).
  const stripped = str.normalize('NFKD').replace(/[̀-ͯ]/g, '');

  // 2. Lowercase + replace any non [a-z0-9] run with a single hyphen.
  const lowered = stripped.toLowerCase();
  const hyphenated = lowered.replace(/[^a-z0-9]+/g, '-');

  // 3. Trim leading / trailing hyphens.
  const trimmed = hyphenated.replace(/^-+|-+$/g, '');

  if (trimmed.length <= maxLen) return trimmed;

  // 4. Truncate and re-trim trailing hyphen if the cut lands mid-word.
  return trimmed.slice(0, maxLen).replace(/-+$/g, '');
}

/**
 * Given a candidate slug and the set of already-taken slugs in the same
 * namespace, return a unique slug by appending `-2`, `-3`, … as needed.
 *
 *   disambiguate('nov-2026-downtown', ['nov-2026-downtown'])
 *     → 'nov-2026-downtown-2'
 *   disambiguate('nov-2026-downtown', ['nov-2026-downtown', 'nov-2026-downtown-2'])
 *     → 'nov-2026-downtown-3'
 *
 * Empty / null `baseSlug` yields `''` (caller must decide).
 *
 * @param {string} baseSlug
 * @param {Iterable<string>} existingSlugs
 * @param {number} [maxLen=MAX_SLUG_LEN]
 * @returns {string}
 */
function disambiguate(baseSlug, existingSlugs, maxLen = MAX_SLUG_LEN) {
  if (!baseSlug) return '';
  const taken = new Set(existingSlugs || []);
  if (!taken.has(baseSlug)) return baseSlug;

  for (let n = 2; n < 10000; n += 1) {
    const suffix = `-${n}`;
    const room = maxLen - suffix.length;
    const truncatedBase =
      baseSlug.length > room ? baseSlug.slice(0, room).replace(/-+$/g, '') : baseSlug;
    const candidate = `${truncatedBase}${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }

  // Extremely unlikely in practice — fall back to base + timestamp.
  return `${baseSlug}-${Date.now()}`.slice(0, maxLen);
}

const PLAN_TYPE_LABELS = {
  weekly: 'wk',
  monthly: '', // month name implied by periodStart
  quarterly: '', // quarter implied by periodStart
  annual: '', // year implied by periodStart
};

const MONTH_ABBR = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];

function safeYearMonth(dateStr) {
  if (!dateStr) return null;
  const parts = String(dateStr).split('T')[0].split('-');
  if (parts.length < 2) return null;
  const year = parseInt(parts[0], 10);
  const monthIdx = parseInt(parts[1], 10) - 1;
  if (Number.isNaN(year) || Number.isNaN(monthIdx) || monthIdx < 0 || monthIdx > 11) return null;
  return { year, monthIdx };
}

/**
 * Build a canonical sales-plan slug from plan + location fields. The result
 * is always a valid slug (may be empty if every field is unusable).
 *
 *   buildPlanSlug({ planType:'monthly', periodStart:'2026-11-02', periodEnd:'2026-11-29',
 *                   locationName:'Downtown Retail', versionLabel:null })
 *     → 'nov-2026-downtown-retail'
 *
 *   buildPlanSlug({ planType:'quarterly', periodStart:'2026-10-01', periodEnd:'2026-12-31',
 *                   locationName:'Downtown Retail', versionLabel:'v2' })
 *     → 'q4-2026-downtown-retail-v2'
 *
 *   buildPlanSlug({ planType:'weekly', periodStart:'2026-11-02', periodEnd:'2026-11-08',
 *                   locationName:'Uptown', versionLabel:null })
 *     → 'wk-2026-11-02-uptown'
 *
 *   buildPlanSlug({ planType:'annual', periodStart:'2027-01-01', periodEnd:'2027-12-31',
 *                   locationName:'Downtown', versionLabel:null })
 *     → '2027-annual-downtown'
 *
 * @param {Object} plan
 * @param {string} plan.planType - 'weekly' | 'monthly' | 'quarterly' | 'annual'
 * @param {string} plan.periodStart - YYYY-MM-DD
 * @param {string} [plan.periodEnd] - YYYY-MM-DD (used only for weekly when needed)
 * @param {string} [plan.locationName]
 * @param {string|null} [plan.versionLabel]
 * @param {number} [maxLen=MAX_SLUG_LEN]
 * @returns {string}
 */
function buildPlanSlug(plan, maxLen = MAX_SLUG_LEN) {
  if (!plan || typeof plan !== 'object') return '';

  const { planType, periodStart, locationName, versionLabel } = plan;
  const ym = safeYearMonth(periodStart);

  // Temporal anchor is required — without it we can't generate a meaningful
  // slug. Weekly plans use the raw date; every other planType needs a
  // parsable year+month.
  let temporalPart = null;
  if (planType === 'weekly' && periodStart && ym) {
    temporalPart = `wk-${periodStart}`;
  } else if (planType === 'quarterly' && ym) {
    const quarter = Math.floor(ym.monthIdx / 3) + 1; // 1..4
    temporalPart = `q${quarter}-${ym.year}`;
  } else if (planType === 'annual' && ym) {
    temporalPart = `${ym.year}-annual`;
  } else if (ym) {
    // 'monthly' AND any unknown planType both render month-year — one
    // branch, not two identical ones (CLOSURE-G2 mutation adjudication:
    // a dedicated `planType === 'monthly'` arm was byte-identical to this
    // fallback, making its condition mutants unkillable).
    temporalPart = `${MONTH_ABBR[ym.monthIdx]}-${ym.year}`;
  }

  if (!temporalPart) return '';

  const parts = [temporalPart];
  if (locationName) parts.push(locationName);
  if (versionLabel) parts.push(versionLabel);

  // Reserve suffix room so disambiguate() has breathing space.
  const raw = parts.join('-');
  return slugify(raw, Math.max(8, maxLen - 5));
}

/**
 * Build a canonical location slug from name (preferred) or code (fallback).
 *
 *   buildLocationSlug({ name: 'Store 09', code: 'STORE09' })
 *     → 'store-09'
 *   buildLocationSlug({ name: null, code: 'STORE09' })
 *     → 'store09'
 *
 * The slug is a pure function on `name` / `code`. `Location.create` pairs this
 * with `disambiguate()` so collisions become `store-09-2`, `store-09-3`, …
 * until a free slot is found in the tenant `locations` table.
 *
 * @param {Object} location
 * @param {string} [location.name] - Human-readable location name (preferred slug source)
 * @param {string} [location.code] - Optional short code (e.g. 'STORE09') used when name is absent
 * @param {number} [maxLen=MAX_SLUG_LEN]
 * @returns {string}
 */
function buildLocationSlug(location, maxLen = MAX_SLUG_LEN) {
  if (!location || typeof location !== 'object') return '';
  const { name, code } = location;
  // Prefer name (human readable), fall back to code. Coerce numeric codes.
  let base = '';
  if (name !== null && name !== undefined && String(name).length > 0) {
    base = String(name);
  } else if (code !== null && code !== undefined && String(code).length > 0) {
    base = String(code);
  }
  // NOTE: no `if (!base) return ''` fast-path — slugify('') already returns
  // '' (CLOSURE-G2 mutation adjudication, the guard was provably dead code).
  // Reserve 5 chars for a disambiguator tail (`-NNN`) so two locations with
  // identical names still fit under the VARCHAR(80) ceiling after suffixing.
  return slugify(base, Math.max(8, maxLen - 5));
}

/** Lowercase 3-letter month abbreviations, indexed by month number − 1. */
const MONTH_SLUG_ABBR = Object.freeze([
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
]);

/**
 * Build a canonical schedule-draft slug from the draft's ALTITUDE + period
 * anchor + location.
 *
 *   buildScheduleDraftSlug({ planningUnit: 'month', periodStart: '2026-07-01', locationName: 'Store 09' })
 *     → 'jul-2026-store-09'
 *   buildScheduleDraftSlug({ planningUnit: 'week',  periodStart: '2026-11-02', locationName: 'Downtown Retail' })
 *     → 'wk-2026-11-02-downtown-retail'
 *   buildScheduleDraftSlug({ planningUnit: 'day',   periodStart: '2026-07-09', locationName: 'Store 09' })
 *     → 'd-2026-07-09-store-09'
 *
 * PLN-SCHED-PERIOD-001 P3.2: the input was `weekStart` and every slug carried a
 * `wk-` prefix, so a MONTH draft was slugged `wk-2026-07-01-store-09` — a URL
 * that claims a week and delivers a month. Three such rows existed in
 * production. The slug now names the altitude it actually has, and is sourced
 * from `periodStart`, which is `NOT NULL` at every altitude.
 *
 * Slug content MUST be immutable post-create — `status`, `versionLabel`,
 * `publishedAt` etc. are deliberately excluded so the URL survives the
 * draft → review → published transitions. The ONE act that re-mints it is
 * `extend-period`, which is the only operation that changes `planningUnit` or
 * `periodStart`; that act is audited and carries the previous slug.
 *
 * @param {Object} draft
 * @param {('day'|'week'|'month')} draft.planningUnit
 * @param {string} draft.periodStart - YYYY-MM-DD
 * @param {string} [draft.locationName]
 * @param {number} [maxLen=MAX_SLUG_LEN]
 * @returns {string} Empty string when `periodStart` is missing/malformed.
 */
function buildScheduleDraftSlug(draft, maxLen = MAX_SLUG_LEN) {
  if (!draft || typeof draft !== 'object') return '';
  const { planningUnit, periodStart, locationName } = draft;
  const ym = safeYearMonth(periodStart);
  if (!periodStart || !ym) return '';

  // Anchor on the ISO date prefix so `2026-11-02T00:00:00Z` yields the same
  // slug as `2026-11-02`.
  const datePrefix = String(periodStart).split('T')[0];
  const [year, month] = datePrefix.split('-');

  let anchor;
  if (planningUnit === 'month') {
    anchor = `${MONTH_SLUG_ABBR[Number(month) - 1]}-${year}`;
  } else if (planningUnit === 'day') {
    anchor = `d-${datePrefix}`;
  } else {
    // `week` — and the historical default for a draft with no explicit altitude.
    anchor = `wk-${datePrefix}`;
  }

  const parts = [anchor];
  if (locationName) parts.push(String(locationName));
  return slugify(parts.join('-'), Math.max(8, maxLen - 5));
}

/**
 * Build a canonical shift-template slug from name + location.
 *
 *   buildShiftTemplateSlug({ name: 'Opening Shift', locationName: 'Downtown Retail' })
 *     → 'opening-shift-downtown-retail'
 *
 * Shift templates have no period; slug is `<name>-<location>`. Name is
 * required. Slug content MUST be immutable post-create — `isActive`,
 * `startTime`, `breaks` etc. are deliberately excluded.
 *
 * @param {Object} template
 * @param {string} template.name - Template display name (required)
 * @param {string} [template.locationName]
 * @param {number} [maxLen=MAX_SLUG_LEN]
 * @returns {string}
 */
function buildShiftTemplateSlug(template, maxLen = MAX_SLUG_LEN) {
  if (!template || typeof template !== 'object') return '';
  const { name, locationName } = template;
  if (name === null || name === undefined || String(name).length === 0) return '';
  const parts = [String(name)];
  if (locationName !== null && locationName !== undefined && String(locationName).length > 0) {
    parts.push(String(locationName));
  }
  return slugify(parts.join('-'), Math.max(8, maxLen - 5));
}

/**
 * Build a canonical labor-budget slug from the period anchor + location.
 *
 *   buildLaborBudgetSlug({ periodStart: '2026-11-02', locationName: 'Downtown Retail' })
 *     → 'bdg-2026-11-02-downtown-retail'
 *
 * PLN-SCHED-PERIOD-001 P2.3 renamed the input from `weekStart` and replaced the
 * `wk-` prefix with `bdg-`. A labor budget is NOT week-anchored: the schedule
 * builder provisions a month draft's budget spanning the whole month, so both
 * the old field name and the old prefix asserted a duration the row does not have.
 *
 * Slug content MUST be immutable post-create — `status`, `splhTarget`,
 * `fixedHours`, `approvedBy` etc. are deliberately excluded.
 *
 * @param {Object} budget
 * @param {string} budget.periodStart - YYYY-MM-DD
 * @param {string} [budget.locationName]
 * @param {number} [maxLen=MAX_SLUG_LEN]
 * @returns {string}
 */
function buildLaborBudgetSlug(budget, maxLen = MAX_SLUG_LEN) {
  if (!budget || typeof budget !== 'object') return '';
  const { periodStart, locationName } = budget;
  const ym = safeYearMonth(periodStart);
  if (!periodStart || !ym) return '';
  const datePrefix = String(periodStart).split('T')[0];
  const parts = [`bdg-${datePrefix}`];
  if (locationName) parts.push(String(locationName));
  return slugify(parts.join('-'), Math.max(8, maxLen - 5));
}

module.exports = {
  slugify,
  disambiguate,
  buildPlanSlug,
  buildLocationSlug,
  buildScheduleDraftSlug,
  buildShiftTemplateSlug,
  buildLaborBudgetSlug,
  MAX_SLUG_LEN,
  // Exposed for testability / callers that want canonical month abbreviations
  MONTH_ABBR,
  PLAN_TYPE_LABELS,
};
